import React, { useState } from 'react';
import { TrendingUp, Eye, Clock, X, User } from 'lucide-react';
import { Button } from './ui/button';
import { blogPosts } from '../data/mock';
import { BlogComments } from './BlogComments';

export const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  const openPost = (post) => {
    setSelectedPost(post);
  };

  const closePost = () => {
    setSelectedPost(null);
  };

  return (
    <>
      {/* Blog Post Reader Modal */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto" data-testid="blog-modal">
          <div className="min-h-screen py-12 px-4">
            <div className="max-w-4xl mx-auto">
              {/* Close Button */}
              <button
                onClick={closePost}
                className="fixed top-6 right-6 z-50 w-12 h-12 bg-gunmetal-800/80 backdrop-blur-sm rounded-full flex items-center justify-center text-steel-300 hover:text-white hover:bg-accent-red transition-all duration-300"
                data-testid="blog-close-btn"
              >
                <X size={24} />
              </button>

              {/* Hero Image */}
              <img
                src={selectedPost.image}
                alt={selectedPost.title}
                className="w-full h-64 md:h-80 object-cover rounded-t-lg"
              />

              {/* Reading Card — solid background for proper contrast */}
              <article className="bg-gunmetal-900 border border-steel-700/40 rounded-b-lg shadow-2xl shadow-black/60 px-6 md:px-12 py-10">
                {/* Header */}
                <div className="mb-8 pb-6 border-b border-steel-700/40">
                  <h1 className="text-3xl md:text-5xl font-black text-steel-100 mb-4 leading-tight">
                    {selectedPost.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-steel-400 text-sm">
                    <span className="flex items-center space-x-1">
                      <User size={16} />
                      <span className="text-steel-200 font-semibold">{selectedPost.author}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock size={16} />
                      <span>{selectedPost.readTime}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Eye size={16} />
                      <span>{selectedPost.views} views</span>
                    </span>
                    <span className="px-3 py-1 bg-accent-red/20 text-accent-red rounded-full text-xs font-semibold">
                      {selectedPost.category}
                    </span>
                  </div>
                </div>

                {/* Post Content — high-contrast reading */}
                <div className="max-w-2xl mx-auto">
                  {selectedPost.content.split('\n\n').map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-steel-100 text-lg leading-[1.8] mb-6 whitespace-pre-wrap font-light"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Comments — open the debate */}
                <div className="max-w-2xl mx-auto">
                  <BlogComments postId={String(selectedPost.id)} />
                </div>

                {/* Back Button */}
                <div className="mt-12 text-center">
                  <Button
                    onClick={closePost}
                    className="bg-accent-red hover:bg-accent-red-dark text-white font-bold px-8 py-4"
                    data-testid="blog-back-btn"
                  >
                    Back to Blog
                  </Button>
                </div>
              </article>
            </div>
          </div>
        </div>
      )}

      <section id="blog" className="relative py-24 bg-gunmetal-950">
        {/* Graffiti Brick Wall Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/5625918/pexels-photo-5625918.jpeg)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-950/90 via-gunmetal-900/95 to-gunmetal-950"></div>
          <div className="absolute inset-0 opacity-30 noise-texture"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header with Graffiti Style */}
          <div className="text-center mb-16 animate-fade-in-up">
            {/* Graffiti Title */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-4 graffiti-text">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-500 to-yellow-400 drop-shadow-[0_0_15px_rgba(255,215,0,0.8)] tracking-wider transform -rotate-2 inline-block">
                WHATCHU
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 drop-shadow-[0_0_15px_rgba(255,0,0,0.8)] tracking-wider transform rotate-1 inline-block mt-2">
                TAWKIN' BOUT?
              </span>
            </h2>
            <div className="w-32 h-2 bg-accent-red mx-auto mb-6 transform -rotate-1"></div>
            <p className="text-xl md:text-2xl text-steel-300 max-w-3xl mx-auto font-bold">
              Brooklyn Brownstone Realness • Street Philosophy • No Filter
            </p>
            <p className="text-steel-500 mt-2">Top Trending Topics of the Day</p>
          </div>

          {/* Trending Posts Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {blogPosts.filter(post => post.trending).map((post, index) => (
              <div
                key={post.id}
                className="group bg-gradient-to-br from-gunmetal-800/70 to-gunmetal-900/70 backdrop-blur-sm rounded-lg overflow-hidden border-2 border-steel-700/30 hover:border-accent-red transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20 cursor-pointer"
                onClick={() => openPost(post)}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Trending Badge */}
                <div className="absolute top-4 left-4 z-10 bg-accent-red px-3 py-1 rounded-full flex items-center space-x-1 animate-pulse-subtle">
                  <TrendingUp size={16} className="text-white" />
                  <span className="text-white font-bold text-sm">TRENDING #{index + 1}</span>
                </div>

                {/* Post Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-950 via-transparent to-transparent"></div>
                </div>

                {/* Post Content */}
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-accent-red/20 text-accent-red rounded-full text-xs font-semibold mb-3">
                    {post.category}
                  </span>

                  <h3 className="text-xl font-bold text-steel-100 mb-3 group-hover:text-accent-red transition-colors duration-300 leading-tight">
                    {post.title}
                  </h3>

                  <p className="text-steel-400 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Post Meta */}
                  <div className="flex items-center justify-between text-xs text-steel-500">
                    <span className="flex items-center space-x-1">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Eye size={14} />
                      <span>{post.views}</span>
                    </span>
                    <span>{post.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Street Quote */}
          <div className="bg-gunmetal-900/80 backdrop-blur-sm rounded-lg p-8 md:p-12 border-l-4 border-accent-red relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-accent-red/5 to-transparent"></div>
            <div className="relative z-10">
              <blockquote className="text-2xl md:text-3xl font-black text-steel-200 italic mb-4">
                "Real talk, no cap. We keep it 💯 on these digital streets."
              </blockquote>
              <p className="text-steel-400 text-lg">
                This ain't your typical self-help blog. We serve truth with a side of reality check.
                Brooklyn style. No sugar-coating, just straight wisdom from the trenches.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
